import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import TopBanner from '@/components/back-office/TopBanner';
import BOLayout from '@/pages/back-office/BOLayout';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pen from '@/assets/media/SVGComponents/Pen';
import Trash from '@/assets/media/SVGComponents/Trash';
import Modal from '@/components/Modal';
import Pagination from '@/components/back-office/Pagination';
import { formatPromoDate } from '@/utils/dateUtils';

const PromoDashboard = () => {
    const { restaurantId } = useParams();
    const [promos, setPromos] = useState([]);
    const role = JSON.parse(localStorage.getItem('role'));
    const [showModal, setShowModal] = useState(false);
    const [promoId, setPromoId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showReferralPromos, setShowReferralPromos] = useState(false);
    const promosPerPage = 10;
    const navigate = useNavigate();

    useEffect(() => {
        if (role && !['super-admin', 'admin'].includes(role)) {
            toast.info('Zone interdite', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
            navigate('/back-office');
            return;
        }
    }, [role, navigate]);

    useEffect(() => {
        const fetchPromos = async () => {
            try {
                const url = showReferralPromos
                    ? `${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos?includeReferralPromos=true`
                    : `${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos`;
                const response = await axios.get(url);
                if (response.status === 200) {
                    setPromos(response.data);
                } else {
                    toast.error('Une erreur est survenue', {
                        position: 'top-center',
                        autoClose: 3500,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: false,
                        draggable: true,
                        theme: 'colored',
                    });
                }
            } catch (error) {
                if (error.response?.status === 404) {
                    setPromos([]);
                    return;
                }
                toast.error('Erreur lors du chargement des codes promo', {
                    position: 'top-center',
                    autoClose: 1000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: true,
                    theme: 'colored',
                });
            }
        };
        fetchPromos();
    }, [restaurantId, showReferralPromos]);

    useEffect(() => {
        setCurrentPage(1);
    }, [showReferralPromos]);

    const handleDelete = async () => {
        try {
            await axios.delete(`${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos/${promoId}`);
            toast.success('Code promo supprimé', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
            setPromos(promos.filter((promo) => promo.id !== promoId));
        } catch (error) {
            toast.error('Une erreur est survenue lors de la suppression', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }
        setShowModal(false);
    };

    const handleOpenModal = (promoId) => {
        setPromoId(promoId);
        setShowModal(true);
    };

    const isReferralOrReorderPromo = (promo) =>
        promo.is_reorder_discount === true ||
        promo.is_30days_reorder === true ||
        String(promo.reference || '').startsWith('REORDER30-') ||
        String(promo.reference || '').startsWith('REORDER-');

    const displayedPromos = showReferralPromos ? promos : promos.filter((p) => !isReferralOrReorderPromo(p));

    const indexOfLastPromo = currentPage * promosPerPage;
    const indexOfFirstPromo = indexOfLastPromo - promosPerPage;
    const currentPromos = displayedPromos.slice(indexOfFirstPromo, indexOfLastPromo);

    const onPageChange = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <BOLayout>
            <div className="mb-4">
                <TopBanner
                    role={role}
                    showButtons={'left'}
                    showToggle={false}
                    navigationRouteText={'Créer un code promo'}
                    navigationRoute={`/back-office/restaurants/${restaurantId}/promos/creation`}
                    hasTopRightButton={['super-admin', 'admin'].includes(role)}
                />
            </div>

            {showModal && (
                <Modal
                    type={'danger'}
                    title={'Cette action est irréversible'}
                    setShowModal={setShowModal}
                    handleSubmit={handleDelete}
                />
            )}

            <div className="flex items-center gap-3 mb-4">
                <span className="text-sm font-medium">Afficher les codes promo parrainage</span>
                <Switch
                    checked={showReferralPromos}
                    onChange={setShowReferralPromos}
                    className={`${
                        showReferralPromos ? 'bg-primary' : 'bg-gray-300'
                    } relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors`}
                >
                    <span className="sr-only">Afficher les codes promo parrainage</span>
                    <span
                        className={`${
                            showReferralPromos ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                    />
                </Switch>
            </div>

            <table className="table table-striped table-bordered table-fixed w-full text-center">
                <thead className="border-b border-b-black pb-2 mb-4">
                    <tr>
                        <th>Référence</th>
                        <th>Type</th>
                        <th>Valeur</th>
                        <th>Montant minimum</th>
                        <th>Date de début</th>
                        <th>Date de fin</th>
                        <th>Statut</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentPromos.length > 0 ? currentPromos.map((promo) => {
                        const isGlobalPromo = promo.restaurantId == null || isReferralOrReorderPromo(promo);
                        return (
                            <tr key={promo.id}>
                                <td>{promo.reference}</td>
                                <td>{promo.discountType}</td>
                                <td>{promo.discountValue}</td>
                                <td>{promo.minOrderValue}</td>
                                <td>{formatPromoDate(promo.startDate)}</td>
                                <td>{formatPromoDate(promo.endDate)}</td>
                                <td>{promo.active ? 'Actif' : 'Inactif'}</td>
                                <td className="flex items-center justify-center gap-2">
                                    {!isGlobalPromo && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate(`/back-office/restaurants/${restaurantId}/promos/${promo.id}/edition`)
                                                }
                                            >
                                                <Pen color="#A2924A" width={40} height={40} />
                                            </button>
                                            <button
                                                type="button"
                                                className="m-2"
                                                onClick={() => handleOpenModal(promo.id)}
                                            >
                                                <Trash color="#A2924A" width={40} height={40} />
                                            </button>
                                        </>
                                    )}
                                    {isGlobalPromo && <span className="text-gray-400 text-sm">—</span>}
                                </td>
                            </tr>
                        );
                    }) : (
                        <tr>
                            <td colSpan="8" className="border px-4 py-2 text-center">
                                Aucun code promo disponible.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Composant Pagination */}
            <Pagination
                currentPage={currentPage}
                totalItems={displayedPromos.length}
                itemsPerPage={promosPerPage}
                onPageChange={onPageChange}
            />
        </BOLayout>
    );
};

export default PromoDashboard;
